/* ---------------------------------------------------------------------------
   The footprint of a deck, for drawing on the map.

   A pin says where a structure is. At the zoom a click now takes you to, a pin is
   the least interesting thing on screen: what you want to see is the bridge — how
   long it is against the crossing, which way it runs, how it sits between the
   banks. That needs a shape, and a shape needs three things the register has and
   one it does not.

   IT HAS the position (`lat`/`lng`), the total length and the overall deck width —
   the last two off the as-built drawings, which is the best data in the register.
   IT DOES NOT have an orientation. Nothing on a DoR inventory sheet or a General
   Arrangement drawing records which way a bridge points.

   So the axis is `deckBearing` on the record, beside the coordinates, and it is
   maintained the same way: seeded from OpenStreetMap — the bearing between the
   endpoints of the `bridge=yes` way at each crossing, queried once through
   Overpass, see the DECK BEARINGS note in src/data/seed.ts — and thereafter
   whatever someone has dragged it to on the map. Seeded rather than fetched
   because a third-party API has no business in the render path of a screen that
   has to work on one bar of signal.

   WHAT IS DRAWN IS THEREFORE A HYBRID, and it is worth being exact about which
   half is which:

     · size    — the register's own `length` × `deckWidth`, off the drawings
     · axis    — OSM, ±a degree or two, and only the AXIS: a deck has no direction,
                 so these are normalised to [0°, 180°)
     · centre  — the record's map pin, which is a Google Maps pin (see the
                 COORDINATES note in src/data/seed.ts)

   It is a scale diagram of the recorded structure, laid over the crossing at the
   right angle. It is NOT a survey of the deck, the outline of what is actually
   there, or a footprint anything may be set out from. Where the pin is off by
   fifteen metres, so is this — the drawing is only as good as its centre.

   The OSM ways were also a useful cross-check on the register, and two of them
   disagreed with it — both recorded in seed.ts, neither resolved.
   --------------------------------------------------------------------------- */

import type { BridgeView } from '../data/types.ts';
import type { LngLat } from '../data/nepal.ts';

/** Metres per degree of latitude. Good to a few parts in a thousand anywhere. */
const M_PER_DEG = 111_320;

/**
 * Normalise an axis into [0°, 180°).
 *
 * A deck has an axis, not a heading: 20° and 200° are the same bridge seen from
 * two ends. Everything that produces a bearing — the OSM query the seed values
 * came from, and the rotate handle on the map — goes through this, so there is one
 * representation in the register and `179.9 → 0.1` is a rotation of 0.2° rather
 * than of 179.8°.
 */
export function normaliseAxis(deg: number): number {
  return ((deg % 180) + 180) % 180;
}

/**
 * The four corners of a structure's deck, as longitude/latitude.
 *
 * Returned in order, so a consumer can draw it as a closed polygon: the two
 * corners at one end, then the two at the other. Null when the structure has no
 * position or no known axis — the two cases where there is nothing honest to
 * draw.
 *
 * Flat-earth arithmetic, deliberately: the largest of these is 200 m, over which
 * the difference between this and a proper geodesic is millimetres, and the
 * result is about to be rounded to a tenth of a pixel.
 */
export function deckFootprint(
  b: BridgeView,
  override?: { lat: number; lng: number; bearing: number },
): readonly LngLat[] | null {
  const lat = override?.lat ?? b.lat;
  const lng = override?.lng ?? b.lng;
  const axis = override?.bearing ?? b.deckBearing;
  /* A FOOTPRINT NEEDS FOUR THINGS AND HAS NO DEFAULT FOR ANY OF THEM. Position
     and axis were always required; length and width joined them when the
     register learned to say it does not know. A footprint drawn at a nominal
     size is a rectangle on a map that says a structure is that big — which is a
     measurement, from nothing, over a satellite image. Null instead, and the map
     draws the pin without the outline, exactly as it already does for a
     structure whose bearing nobody has established. */
  if (lat == null || lng == null || axis == null) return null;
  if (b.length == null || b.deckWidth == null) return null;

  const th = (axis * Math.PI) / 180;
  const halfLength = b.length / 2;
  const halfWidth = b.deckWidth / 2;

  // Unit vectors in metres: along the deck, and across it.
  const along = { e: Math.sin(th), n: Math.cos(th) };
  const across = { e: Math.cos(th), n: -Math.sin(th) };

  const corners: [number, number][] = [
    [+halfLength, +halfWidth],
    [+halfLength, -halfWidth],
    [-halfLength, -halfWidth],
    [-halfLength, +halfWidth],
  ];

  /* One scale for longitude, taken at the CENTRE's latitude — a local tangent
     plane, in other words. Scaling each corner at its own latitude looks more
     careful and is worse: the north corners would then be offset by a slightly
     different amount from the south ones, which leaves the shape a fraction
     off-centre from the pin it is drawn around. The error either way is under a
     millimetre over 200 m; only one of them is exactly centred, and being exactly
     centred is a property worth being able to assert. */
  const lngPerMetre = 1 / (M_PER_DEG * Math.cos((lat * Math.PI) / 180));

  return corners.map(([l, w]) => {
    const north = l * along.n + w * across.n;
    const east = l * along.e + w * across.e;
    return [lng + east * lngPerMetre, lat + north / M_PER_DEG] as LngLat;
  });
}
